import { ReactElement, useState } from 'react';
import { StatusBar, Modal, PrimaryButton, SecondaryButton, Spinner, Typography } from '@weareopenr/component-library';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import classnames from 'classnames/bind';
import MealtimesView from './MealtimesView';
import PublishView from './PublishView';
import SummaryView from './SummaryView/SummaryView';
import { NoChannelsView } from './NoChannelsView/NoChannelsView';
import { useExternalSystemPublishing } from '../../hooks/useExternalSystemPublishing';
import { getModalTitle } from 'src/helpers/getModalTitle';
import filterMealtimesWithCategories from './utilities/filterMealtimesWithCategories';
import ModalImg from '../../assets/images/chef-plating.png';
import { IModalProps, IPublishDestination, PublishSteps } from './types';
import styles from './PublishModal.module.scss';
import { FieldValues, useForm } from 'react-hook-form';
import SchemeView from './SchemeView';
import { IPublishMenusProps } from 'src/api/publishMenu/types';
import { errorNotification, successNotification } from './PublishView/Notifications';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useNavigate } from 'react-router-dom';
import { usePublishMenu } from 'src/hooks/usePublishMenu/usePublishMenu';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { formatScheduledTime, publishTimes } from '../ScheduleView/shared';
import { IMenu } from 'src/types/menu.types';
import { ScheduleSelector } from '../ScheduleView/ScheduleView';
import dayjs from 'dayjs';

const cx = classnames.bind(styles);

const standardSteps = [
  PublishSteps.Mealtimes,
  PublishSteps.Scheme,
  PublishSteps.Channels,
  PublishSteps.DateTime,
  PublishSteps.Overview,
];

const trialSteps = [PublishSteps.Mealtimes, PublishSteps.Scheme, PublishSteps.Channels, PublishSteps.Overview];

const PublishFlowWrapper = ({
  menuId,
  isTrial,
  closeModal,
}: {
  menuId: string;
  isTrial: boolean;
  closeModal: () => void;
}) => {
  const steps = isTrial ? trialSteps : standardSteps;

  const { data: menu, isLoading: isMenuLoading } = useMenu(menuId);
  const {
    data: publicationData,
    isLoading: isPublicationDataLoading,
    isError,
  } = useExternalSystemPublishing(isTrial, menu?.publication);

  if (isMenuLoading || isPublicationDataLoading) {
    return (
      <div className={styles['publish-flow']}>
        <Spinner size="large" data-testid="publish-loading" />
      </div>
    );
  }

  if (!publicationData || publicationData.length === 0 || isError) {
    return (
      <div className={styles['publish-flow']}>
        <NoChannelsView />
      </div>
    );
  }

  return (
    <PublishFlow menuId={menuId} steps={steps} closeModal={closeModal} publicationData={publicationData} menu={menu} />
  );
};

const PublishFlow = ({
  menuId,
  steps,
  closeModal,
  publicationData,
  menu,
}: {
  menuId: string;
  steps: PublishSteps[];
  publicationData: IPublishDestination[];
  menu: IMenu;
  closeModal: () => void;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { trackEvent, captureException } = useUserTracking();

  const publishMenu = usePublishMenu(menuId);
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const [step, setStep] = useState(PublishSteps.Mealtimes);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultSelectedChannelVenues = Object.fromEntries(
    publicationData.map((channel) => [
      channel.channel_id,
      Object.fromEntries(channel.venues.map((venue) => [venue.venue_id, menu.linkedVenueIds.has(venue.venue_id)])),
    ]),
  );

  const defaultValues = {
    'price-scheme': undefined,
    publicationType: publishTimes.NOW,
    publicationDate: new Date().toISOString(),
    mealtimes: 'disabled',
    channels: defaultSelectedChannelVenues,
  };

  const formFields = useForm<FieldValues>({
    defaultValues,
  });
  const { control, watch, register, handleSubmit, setValue } = formFields;

  const filteredMealtimes = filterMealtimesWithCategories(menu.mealtimes);

  const getButtonDisabledState = (values: FieldValues, step: PublishSteps) => {
    switch (step) {
      case PublishSteps.Scheme:
        return values['price-scheme'] === undefined;
      case PublishSteps.Channels: {
        const channelValues: Array<Record<string, Record<string, boolean>>> = Object.values(values['channels']);
        const channelEnabled = channelValues.reduce((acc, curr) => Object.values(curr).some(Boolean) || acc, false);
        return !channelEnabled;
      }
      case PublishSteps.DateTime:
        return values['publicationType'] === publishTimes.SCHEDULE && !values['confirmationCheckbox'];
      case PublishSteps.Overview:
        return isSubmitting;
      default:
        return false;
    }
  };

  const decrementStep = () => {
    const index = steps.indexOf(step) - 1;
    const prevStep = steps.at(index);
    return setStep(prevStep);
  };

  const incrementStep = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const index = steps.indexOf(step) + 1;
    const nextStep = steps.at(index);
    return setStep(nextStep);
  };

  const closeBackAction = step === PublishSteps.Mealtimes ? closeModal : decrementStep;

  const channelNameDict = publicationData.map(({ channel_id, name }) => [channel_id, name]);

  const values = watch();
  const continueDisabled = getButtonDisabledState(values, step);

  const isMealtimeOptionEnabled = values['mealtimes'] === 'enabled';
  const handlePublish = async (values: FieldValues) => {
    clearError();
    setIsSubmitting(true);
    const entries = Object.entries(values['channels']);
    const channels: IPublishMenusProps[] = entries
      .map(([channel_id, venues]) => ({
        channel_id,
        venues: Object.entries(venues)
          .filter(([, value]) => Boolean(value))
          .map(([key]) => key),
      }))
      .filter((f) => f.venues.length > 0);

    const selectedChannels = new Set(channels.map((c) => c.channel_id));
    const names = channelNameDict
      .filter(([channel_id]) => selectedChannels.has(channel_id))
      .map((channel) => channel.at(1));

    const publication_date_time = formatScheduledTime(values['publicationType'], dayjs(values['publicationDate']));

    try {
      await publishMenu.mutateAsync({
        price_scheme_id: values['price-scheme'],
        use_mealtimes: values['mealtimes'] === 'enabled',
        ...(publication_date_time && { publication_date_time: new Date(publication_date_time).toISOString() }),
        channels,
      });

      const isScheduled = values['publicationType'] === publishTimes.SCHEDULE;
      trackEvent('menu_published', {
        menuId,
        menuName: menu.name,
        channelCount: channels.length,
        useMealtimes: values['mealtimes'] === 'enabled',
        isScheduled,
      });

      showNotification(successNotification(menu.name, names, publication_date_time));
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (isScheduled) {
        closeModal();
      } else {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        navigate(`/menus/${menuId}/status`);
      }
    } catch (error) {
      setIsSubmitting(false);
      captureException(error, { tags: { action: 'publish_menu', menuId } });
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      return showNotification(errorNotification(menu.name, publication_date_time, errorMsg));
    }
  };

  return (
    <div
      className={cx('publish-flow', {
        ['publish-flow-space']: step === PublishSteps.Mealtimes,
      })}
    >
      <main>
        <form onSubmit={handleSubmit(handlePublish)}>
          {step !== PublishSteps.Overview && (
            <Typography weight="semi-bold" className={styles.header}>
              {getModalTitle(steps.slice(0, -1), step)}
            </Typography>
          )}
          {step === PublishSteps.Mealtimes && (
            <MealtimesView register={register} mealtimes={menu.mealtimes} filteredMealtimes={filteredMealtimes} />
          )}
          {step === PublishSteps.Scheme && <SchemeView control={control} />}
          {step === PublishSteps.Channels && (
            <PublishView register={register} watch={watch} setValue={setValue} publicationData={publicationData} />
          )}
          {step === PublishSteps.DateTime && <ScheduleSelector register={register} watch={watch} />}
          {step === PublishSteps.Overview && (
            <SummaryView
              watch={watch}
              menuId={menuId}
              menuName={menu.name}
              publicationData={publicationData}
              filteredMealtimes={filteredMealtimes}
              sections={menu.sections}
              isMealtimeOptionEnabled={isMealtimeOptionEnabled}
            />
          )}
          <div className={styles['buttons-container']}>
            <SecondaryButton size="full-width" buttonStyle="outline" onClick={closeBackAction} type="button">
              {step === PublishSteps.Mealtimes ? 'Close' : 'Back'}
            </SecondaryButton>
            <PrimaryButton
              onClick={step === PublishSteps.Overview ? undefined : incrementStep}
              type={step === PublishSteps.Overview ? 'submit' : 'button'}
              size="full-width"
              disabled={continueDisabled}
              loading={isSubmitting}
            >
              {isSubmitting ? 'Publishing' : 'Continue'}
            </PrimaryButton>
          </div>
          <div className={styles['error-banner']}>
            {errorMessage && <StatusBar message={errorMessage} variant="error" />}
          </div>
        </form>
      </main>
    </div>
  );
};

const PublishModal = ({ closeModal, isOpen, menuId, isTrial = false }: IModalProps): ReactElement => {
  return (
    <Modal closeModal={closeModal} open={isOpen} image={ModalImg} imageAlt="Picture of a chef plating up">
      <PublishFlowWrapper menuId={menuId} isTrial={isTrial} closeModal={closeModal} />
    </Modal>
  );
};

export default PublishModal;
